import React from "react";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsModal from "../clients/Tools/kit/ToolsModal";
import ToolsReviewItem, { ToolsReviewResolution } from "../clients/Tools/kit/ToolsReviewItem";
import PlaybookReviewService from "../../services/PlaybookReviewService";
import PlaybookDecisionForm from "./PlaybookDecisionForm";

import "./agent-workflow-action.css";

const OUTCOME_DOT_TONES = {
	executed: "success",
	failed: "danger",
	rejected: "neutral",
	interrupted: "warning",
};

class AgentWorkflowAction extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			isConfirmOpen: false,
			isRejecting: false,
		};
	}

	onOpenConfirm = () => {
		this.update({ isConfirmOpen: true });
	};

	onCloseConfirm = () => {
		this.update({ isConfirmOpen: false });
	};

	onExecute = async () => {
		await this.update({ isConfirmOpen: false });
		await this.props.onExecute();
	};

	onStartRejecting = () => {
		this.update({ isRejecting: true });
	};

	onStopRejecting = () => {
		this.update({ isRejecting: false });
	};

	onSaveRejection = async ({ reasonCategory, reason }) => {
		const isSaved = await this.props.onReject({ reasonCategory, reason });
		if (isSaved) await this.update({ isRejecting: false });
	};

	renderPreviewLines({ lines }) {
		if (!lines || lines.length === 0) return null;
		return (
			<ul className="agent-workflow-action__lines">
				{lines.map((line, index) => (
					<li key={index} className="agent-workflow-action__line">
						{line}
					</li>
				))}
			</ul>
		);
	}

	renderRejecting() {
		const { isBusy } = this.props;
		return <PlaybookDecisionForm isRejection choices={null} isBusy={isBusy} onSave={this.onSaveRejection} onBack={this.onStopRejecting} />;
	}

	renderOutcome({ description }) {
		const { state, outcomeText, resultText } = description;
		if (!outcomeText) return null;
		const dotTone = OUTCOME_DOT_TONES[state];

		return (
			<div className={clsx("agent-workflow-action__outcome", `agent-workflow-action__outcome--${state}`)}>
				<span className={`tk-dot tk-dot--${dotTone}`} />
				<span className="agent-workflow-action__outcome-text">
					{outcomeText}
					{resultText && <span className="agent-workflow-action__result">{resultText}</span>}
				</span>
			</div>
		);
	}

	renderActions({ description }) {
		const { canDecide, isBusy, waitingText } = this.props;
		const { isRejecting } = this.state;
		if (!description.isActionable) return null;
		if (!canDecide) return <span className="agent-workflow-action__waiting">{waitingText}</span>;
		if (isRejecting) return this.renderRejecting();
		const isRetry = description.state === "failed";
		const idleLabel = isRetry ? "Try again" : "Execute";
		const buttonLabel = isBusy ? "Executing…" : idleLabel;

		return (
			<div className="agent-workflow-action__buttons">
				<button type="button" className="tk-button tk-button--primary tk-button--compact" onClick={this.onOpenConfirm} disabled={isBusy}>
					<Icon.Zap size={13} />
					{buttonLabel}
				</button>
				<button type="button" className="tk-text-action tk-text-action--muted" onClick={this.onStartRejecting} disabled={isBusy}>
					Reject
				</button>
			</div>
		);
	}

	renderConfirm() {
		const { proposal, isBusy } = this.props;
		const { isConfirmOpen } = this.state;
		const { preview } = proposal;
		const footer = (
			<div className="tk-modal-actions">
				<button type="button" className="tk-button tk-button--secondary" onClick={this.onCloseConfirm}>
					Cancel
				</button>
				<button type="button" className="tk-button tk-button--primary" onClick={this.onExecute} disabled={isBusy}>
					<Icon.Zap size={14} />
					Execute
				</button>
			</div>
		);

		return (
			<ToolsModal open={isConfirmOpen} onClose={this.onCloseConfirm} title="Execute this action?" width={520} footer={footer}>
				<div className="agent-workflow-action__confirm-preview">
					<span className="agent-workflow-action__confirm-title">{preview.title}</span>
					{this.renderPreviewLines({ lines: preview.lines })}
				</div>
			</ToolsModal>
		);
	}

	render() {
		const { proposal } = this.props;
		const { preview } = proposal;
		const description = PlaybookReviewService.describeWorkflowAction({ proposal });
		const resolutionText = "Nothing happens in Workflow until someone executes it.";

		return (
			<ToolsReviewItem id={`proposal-${proposal.id}`} status={description.look} label={preview.title}>
				{this.renderPreviewLines({ lines: preview.lines })}
				{proposal.reason && (
					<div className="agent-run-report__aside">
						<span className="agent-run-report__aside-label">Why</span>
						{proposal.reason}
					</div>
				)}
				<ToolsReviewResolution text={resolutionText} kindLabel="Workflow action">
					<div className="agent-workflow-action__footer">
						{this.renderOutcome({ description })}
						{this.renderActions({ description })}
					</div>
				</ToolsReviewResolution>
				{this.renderConfirm()}
			</ToolsReviewItem>
		);
	}
}

export default AgentWorkflowAction;
